// The wire contract between app, site and backend. Every request and response
// is parsed with these schemas on the receiving side, and tests pin the shapes
// (BACKLOG #17: shape mismatches were TypeFire's most common parity bug).
import { z } from "zod";
import { ANSWERS_MAX, ANSWERS_MIN, ANSWER_MAX, QUESTION_MAX, QUESTIONS_MAX_ANY } from "./limits";

const code = z.string().regex(/^\d{6}$/);
const token = z.string().min(16).max(128).regex(/^[A-Za-z0-9_-]+$/);

/** What the creator submits. Text is trimmed and checked by `validateDraft`. */
/** One question and its answers. */
export const QuestionDraft = z.object({
  question: z.string().max(QUESTION_MAX * 2),
  answers: z.array(z.string().max(ANSWER_MAX * 2)).min(ANSWERS_MIN).max(ANSWERS_MAX),
  multi: z.boolean().default(false),
  /**
   * Edit only: which question of the poll this one was (0-based), absent for a new question,
   * and for each answer which answer it was (null for a new one). With these the room keeps
   * each vote on its own answer through renames, added answers and reordering.
   */
  from: z.number().int().min(0).max(QUESTIONS_MAX_ANY).optional(),
  answerFrom: z.array(z.number().int().min(0).max(ANSWERS_MAX).nullable()).max(ANSWERS_MAX).optional(),
});
export type QuestionDraft = z.infer<typeof QuestionDraft>;

/**
 * A poll: its first question, plus `more` for a question set (Nav, 2026-10-11). A poll without
 * `more` is exactly the one-question poll every older client sends.
 */
export const PollDraft = QuestionDraft.extend({
  more: z.array(QuestionDraft).max(QUESTIONS_MAX_ANY - 1).optional(),
});
export type PollDraft = z.infer<typeof PollDraft>;

/** "scheduled": a Pro poll whose opening time has not come yet. */
export const PollStatus = z.enum(["waiting", "scheduled", "live", "closed", "hidden"]);
export type PollStatus = z.infer<typeof PollStatus>;

/** Everything a screen needs to draw a poll. Broadcast on every change. */
export const Snapshot = z.object({
  code,
  question: z.string(),
  answers: z.array(z.string()),
  multi: z.boolean(),
  status: PollStatus,
  counts: z.array(z.number().int().nonnegative()),
  /** Devices that have voted. For multi polls this is the percentage base. */
  voters: z.number().int().nonnegative(),
  /** Unix ms. Null while waiting for the first vote. */
  closesAt: z.number().nullable(),
  /** True when the creator has hidden results until reveal (Pro). */
  hidden: z.boolean(),
  /** Unix ms the poll was published (absent from older servers). */
  publishedAt: z.number().optional(),
  /** The host's app language when the poll was made; absent on older polls (they preview in English). */
  lang: z.string().max(8).optional(),
  /** Unix ms of the creator's last change, or null if never changed. */
  updatedAt: z.number().nullable().optional(),
  /** Unix ms when the poll and its results are deleted; null when they are kept forever (Pro). */
  keepUntil: z.number().nullable().optional(),
  /** The poll belongs to an account. False means it lives only on the creator's device. */
  owned: z.boolean().optional(),
  /** Host views only: reports paused this poll for voters, while the host's own screens keep working. Absent means false. */
  review: z.boolean().optional(),
  /**
   * The room is under heavy load (a vote surge or a very large audience):
   * clients vote over HTTP, which the API spreads across vote shards, instead
   * of over the room's own socket. Absent means false.
   */
  busy: z.boolean().optional(),
  /**
   * A question set (more than one question). The top-level question, answers and counts are
   * always the current question, so a screen that knows nothing of sets still shows the right one.
   */
  set: z
    .object({
      /** The current question, 0-based. */
      index: z.number().int().nonnegative(),
      total: z.number().int().min(2),
      /** The host moved past the last question: voting is over and the summary shows. */
      finished: z.boolean(),
      /** Every question's text, for the host's list. */
      questions: z.array(z.string()),
      /** Every question's results, once finished. */
      summary: z
        .array(z.object({ question: z.string(), answers: z.array(z.string()), multi: z.boolean(), counts: z.array(z.number().int().nonnegative()), voters: z.number().int().nonnegative() }))
        .optional(),
    })
    .optional(),
  /** Pro presentation settings. Absent on free polls. */
  pro: z
    .object({
      theme: z.string().max(24),
      /** One vote per device fingerprint; voters see a notice first. */
      strict: z.boolean(),
      /** The poll needs a password to view or vote. */
      locked: z.boolean(),
      opensAt: z.number().nullable(),
      closesAtScheduled: z.number().nullable(),
      /** The LivePoll badge is removed. */
      unbranded: z.boolean(),
      /** Vote counts and the total are not shown to the room, only percentages. */
      countHidden: z.boolean().optional(),
      slug: z.string().max(40).nullable(),
    })
    .optional(),
  /**
   * The anonymous-poll voter gate (viral growth plan, 2026-11-03). Absent on owned, Pro and Pass
   * polls, and on anonymous polls under ANON_HEADS_UP_AT voters.
   *   heads_up: 500 or more voters, voting open
   *   grace:    1,000 reached; voting open until graceEndsAt or GRACE_VOTERS
   *   locked:   grace over; voters already counted keep voting, new voters get "host_unlock"
   */
  gate: z
    .object({
      state: z.enum(["heads_up", "grace", "locked"]),
      /** Unix ms the grace ends. Null before it starts. */
      graceEndsAt: z.number().nullable(),
      /** The total voters at which the grace ends (GRACE_VOTERS). */
      graceVoters: z.number().int().nonnegative(),
    })
    .optional(),
  /** Host views only: the live pulse. Absent on voter snapshots. */
  pulse: z
    .object({
      /** New voters in the last 60 seconds. */
      lastMinute: z.number().int().nonnegative(),
      /** Screens connected right now (sockets plus recent HTTP readers). */
      watching: z.number().int().nonnegative(),
      /** The highest PULSE_MILESTONES value reached, or 0. */
      milestone: z.number().int().nonnegative(),
    })
    .optional(),
  /** Increases on every change, so a client can drop an out-of-order snapshot. */
  seq: z.number().int().nonnegative(),
});
export type Snapshot = z.infer<typeof Snapshot>;

// ---- HTTP API -------------------------------------------------------------

/**
 * Where a new poll came from, for the server's counts (growth plan items 1 and 11). Numbers and
 * a known template id only, never content. Optional, and a malformed value is dropped rather than
 * failing the request: measurement never blocks a poll.
 */
/** "direct", or a bare host name: letters, digits, dots and hyphens, with at least one dot. */
export const HOST_SOURCE_REF = /^(?:direct|(?=.{1,60}$)[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+)$/;
/** A path only: a leading slash, then path characters, no query, fragment or scheme. */
export const HOST_SOURCE_LAND = /^\/[A-Za-z0-9/_.~-]{0,79}$/;

export const CreateSource = z.object({
  /** A template id ("classroom/exit-ticket"); the API counts only ids it knows. */
  template: z.string().max(80).optional(),
  /** The creator came from a vote page ("Make your own" or "Ask the next question"). */
  fromVote: z.boolean().optional(),
  /** Polls this device had already made (from its own local list), capped. Not sent under GPC or DNT. */
  prior: z.number().int().min(0).max(1000).optional(),
  /** Whole days since this device's previous poll. Not sent under GPC or DNT. */
  lastDays: z.number().int().min(0).max(100000).optional(),
  /**
   * A new host's first poll (`prior` 0) only, never under GPC or DNT: where they came from, as a
   * referrer host alone ("line.me", "google.com") or "direct", lowercased, at most 60 characters.
   * A malformed value is dropped on its own, never the whole source.
   */
  ref: z
    .string()
    .max(60)
    .transform((v) => v.trim().toLowerCase().replace(/^www\./, ""))
    .pipe(z.string().regex(HOST_SOURCE_REF))
    .optional()
    .catch(undefined),
  /** The site path a new host first landed on ("/templates/contests"), at most 80 characters, no query. Same rules as `ref`. */
  land: z.string().max(80).regex(HOST_SOURCE_LAND).optional().catch(undefined),
});
export type CreateSource = z.infer<typeof CreateSource>;

/** The host's app language ("th", "pt-BR"): the link preview card speaks it. Unknown values are dropped. */
export const PollLang = z.string().regex(/^[a-z]{2}(-[A-Z]{2})?$/);
export const CreatePollRequest = z.object({ draft: PollDraft, source: CreateSource.optional().catch(undefined), lang: PollLang.optional().catch(undefined) });
export type CreatePollRequest = z.infer<typeof CreatePollRequest>;

export const CreatePollResponse = z.object({
  code,
  /** Shown once. The device keeps it; the server keeps only its hash. */
  adminToken: token,
  snapshot: Snapshot,
});
export type CreatePollResponse = z.infer<typeof CreatePollResponse>;

export const AdminAction = z.enum(["close", "reopen", "delete", "reset", "edit", "settings", "reveal", "hide", "goto"]);
export type AdminAction = z.infer<typeof AdminAction>;

/** Poll themes (Pro). Daylight and Paper are light stages; the rest are dark by design.
 *  "brand" is built from the owner's brand color (Nav, 2026-10-05). */
export const THEMES = ["brand", "midnight", "daylight", "aurora", "sunset", "mono", "ocean", "forest", "ember", "neon", "paper"] as const;
export type Theme = (typeof THEMES)[number];
/** Themes that set their own dark stage, so the presenter's light/dark switch has nothing to do. */
export const DARK_STAGE_THEMES: readonly Theme[] = ["brand", "aurora", "sunset", "mono", "ocean", "forest", "ember", "neon"];

/** Pro settings a creator can change (BACKLOG #26). Any subset. */
export const ProSettings = z.object({
  theme: z.enum(THEMES).optional(),
  strict: z.boolean().optional(),
  /** A new password, or null to remove it. 4 to 64 characters. */
  password: z.string().min(4).max(64).nullable().optional(),
  opensAt: z.number().nullable().optional(),
  closesAtScheduled: z.number().nullable().optional(),
  unbranded: z.boolean().optional(),
  countHidden: z.boolean().optional(),
  /** A custom link name, or null to remove it. */
  slug: z.string().min(3).max(40).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/).nullable().optional(),
});
export type ProSettings = z.infer<typeof ProSettings>;

export const AdminRequest = z.object({
  adminToken: token,
  action: AdminAction,
  /** Only for `edit`, and only accepted before the first vote (BACKLOG #20). */
  draft: PollDraft.optional(),
  settings: ProSettings.optional(),
  /** Only for `edit`: the creator confirmed clearing the votes on the questions the room listed. */
  resetOk: z.boolean().optional(),
  /** Only for `goto`: the question to show, 0-based; the set's length means "finished". */
  index: z.number().int().min(0).max(QUESTIONS_MAX_ANY).optional(),
});
export type AdminRequest = z.infer<typeof AdminRequest>;

export const VoteRequest = z.object({
  voterToken: token,
  choices: z.array(z.number().int().nonnegative()).min(1).max(ANSWERS_MAX),
  /** Strict-voting polls only, after the voter accepted the notice. */
  fingerprint: z.string().regex(/^[a-f0-9]{16,64}$/).optional(),
  /** Password-protected polls only. */
  access: z.string().max(200).optional(),
  /** The question this vote is for (question sets). A vote for any other question is refused. */
  q: z.number().int().min(0).max(QUESTIONS_MAX_ANY).optional(),
});
export type VoteRequest = z.infer<typeof VoteRequest>;

export const ErrorCode = z.enum([
  "invalid",
  "not_found",
  "closed",
  "forbidden",
  "rate_limited",
  "blocked_content",
  "voted_already",
  "unavailable_region",
  "pro_required",
  "password_required",
  // Usage protection: the plan's included amounts are nearly used (no overage, ever).
  "capacity",
  // An anonymous poll is past its free voters and grace: the host needs to sign in (free) to let new voters in.
  "host_unlock",
  "paused",
  "mail_quota",
  "server",
]);
export type ErrorCode = z.infer<typeof ErrorCode>;

export const ErrorResponse = z.object({
  error: ErrorCode,
  /** A field-level detail for `invalid` and `blocked_content`; never raw server text. */
  detail: z.string().optional(),
});
export type ErrorResponse = z.infer<typeof ErrorResponse>;

// ---- Realtime (WebSocket) ---------------------------------------------------

export const ClientMessage = z.discriminatedUnion("t", [
  z.object({
    t: z.literal("vote"),
    voterToken: token,
    choices: VoteRequest.shape.choices,
    id: z.string().max(40),
    fingerprint: VoteRequest.shape.fingerprint,
    q: VoteRequest.shape.q,
  }),
  z.object({ t: z.literal("ping") }),
]);
export type ClientMessage = z.infer<typeof ClientMessage>;

export const ServerMessage = z.discriminatedUnion("t", [
  z.object({ t: z.literal("snap"), snapshot: Snapshot }),
  /** Confirms the vote with client id `id`; `choices` is what the room recorded. */
  z.object({ t: z.literal("ack"), id: z.string(), choices: z.array(z.number().int()) }),
  z.object({ t: z.literal("err"), id: z.string().optional(), error: ErrorCode }),
  z.object({ t: z.literal("pong") }),
]);
export type ServerMessage = z.infer<typeof ServerMessage>;
